import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MapSearchPage.module.css";

// JSON 파일 임포트
import animalHospitalsData from "../_data/animalHospitals.json";
import cafesData from "../_data/cafes.json";
import petCafesData from "../_data/petCafes.json";
import petFuneralHomesData from "../_data/petFuneralHomes.json";
import petParksData from "../_data/petParks.json";
import restaurantsData from "../_data/restaurants.json";

// 검색 결과 인터페이스
interface SearchResult {
  id: string;
  name: string;
  address: string;
  type: string;
  lat: number;
  lng: number;
  thumbnail?: string;
}

// 네이버 주소 검색 결과 인터페이스
interface NaverAddressResult {
  title: string;
  link: string;
  category: string;
  description: string;
  telephone: string;
  address: string;
  roadAddress: string;
  mapx: string;
  mapy: string;
}

// 최근 검색 항목 인터페이스 추가
interface RecentSearchItem {
  id: string;
  name: string;
  address: string;
  type: "address" | "store";
  lat: number;
  lng: number;
  storeType?: string; // 매장인 경우에만
  searchedAt: number; // 검색한 시간
}

const MapSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [addressResults, setAddressResults] = useState<NaverAddressResult[]>(
    []
  );
  const [storeResults, setStoreResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 페이지네이션 상태 추가
  const [displayedAddressCount, setDisplayedAddressCount] = useState(5);
  const [displayedStoreCount, setDisplayedStoreCount] = useState(5);
  const ITEMS_PER_PAGE = 5;

  // 모든 매장 데이터를 하나로 합치기
  const allStoreData: SearchResult[] = [
    ...(animalHospitalsData as any[]).map((item: any) => ({
      id: item.id || `hospital-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "동물병원",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
    ...(cafesData as any[]).map((item: any) => ({
      id: item.id || `cafe-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "카페",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
    ...(petCafesData as any[]).map((item: any) => ({
      id: item.id || `petcafe-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "애견카페",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
    ...(petParksData as any[]).map((item: any) => ({
      id: item.id || `petpark-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "애견공원",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
    ...(petFuneralHomesData as any[]).map((item: any) => ({
      id: item.id || `funeral-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "장례식장",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
    ...(restaurantsData as any[]).map((item: any) => ({
      id: item.id || `restaurant-${Math.random()}`,
      name: item.name || "",
      address: item.address || "",
      type: "식당",
      lat: parseFloat(item.y) || 0,
      lng: parseFloat(item.x) || 0,
      thumbnail: item.thumbnail,
    })),
  ].filter((item) => item.name && item.lat && item.lng); // 유효한 데이터만 필터링

  // 컴포넌트 마운트 시 최근 검색어 불러오기
  useEffect(() => {
    const saved = localStorage.getItem("mapRecentSearches");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // 기존 string[] 형태와 새 RecentSearchItem[] 형태 모두 처리
        if (Array.isArray(parsed)) {
          if (parsed.length > 0 && typeof parsed[0] === "string") {
            // 기존 데이터는 초기화
            setRecentSearches([]);
            localStorage.setItem("mapRecentSearches", JSON.stringify([]));
          } else {
            setRecentSearches(parsed as RecentSearchItem[]);
          }
        }
      } catch (error) {
        console.error("최근 검색어 로드 오류:", error);
        setRecentSearches([]);
      }
    }
  }, []);

  // 최근 검색 항목 저장
  const saveRecentSearchItem = (item: RecentSearchItem) => {
    if (!item.name.trim()) return;

    const updated = [
      item,
      ...recentSearches.filter((existing) => existing.id !== item.id),
    ].slice(0, 10); // 최대 10개 저장

    setRecentSearches(updated);
    localStorage.setItem("mapRecentSearches", JSON.stringify(updated));
  };

  // 전체 최근 검색 항목 삭제
  const clearAllRecentSearches = () => {
    setRecentSearches([]);
    localStorage.setItem("mapRecentSearches", JSON.stringify([]));
  };

  // 네이버 지역 검색 API 호출
  const searchNaverPlaces = async (query: string) => {
    try {
      if (!window.naver || !window.naver.maps || !window.naver.maps.Service) {
        console.warn(
          "네이버 지도 API가 로드되지 않았습니다. 기본 지역 검색을 사용합니다."
        );

        // 기본 지역 검색 (주요 지역명 매칭)
        const basicRegions = [
          // 서울특별시
          { name: "서울", lat: 37.5665, lng: 126.978 },
          { name: "서울특별시", lat: 37.5665, lng: 126.978 },
          { name: "강남구", lat: 37.5175, lng: 127.0473 },
          { name: "강동구", lat: 37.5301, lng: 127.1238 },
          { name: "강북구", lat: 37.6396, lng: 127.0254 },
          { name: "강서구", lat: 37.5509, lng: 126.8495 },
          { name: "관악구", lat: 37.4784, lng: 126.9516 },
          { name: "광진구", lat: 37.5384, lng: 127.0823 },
          { name: "구로구", lat: 37.4954, lng: 126.8874 },
          { name: "금천구", lat: 37.4519, lng: 126.8953 },
          { name: "노원구", lat: 37.6542, lng: 127.0568 },
          { name: "도봉구", lat: 37.6688, lng: 127.0471 },
          { name: "동대문구", lat: 37.5744, lng: 127.0396 },
          { name: "동작구", lat: 37.5124, lng: 126.9393 },
          { name: "마포구", lat: 37.5663, lng: 126.9019 },
          { name: "서대문구", lat: 37.5791, lng: 126.9368 },
          { name: "서초구", lat: 37.4837, lng: 127.0324 },
          { name: "성동구", lat: 37.5634, lng: 127.0367 },
          { name: "성북구", lat: 37.5894, lng: 127.0167 },
          { name: "송파구", lat: 37.5145, lng: 127.1059 },
          { name: "양천구", lat: 37.5168, lng: 126.8665 },
          { name: "영등포구", lat: 37.5264, lng: 126.8962 },
          { name: "용산구", lat: 37.5384, lng: 126.9654 },
          { name: "은평구", lat: 37.6027, lng: 126.9297 },
          { name: "종로구", lat: 37.5735, lng: 126.979 },
          { name: "중구", lat: 37.5641, lng: 126.9979 },
          { name: "중랑구", lat: 37.6063, lng: 127.0925 },

          // 부산광역시
          { name: "부산", lat: 35.1796, lng: 129.0756 },
          { name: "부산광역시", lat: 35.1796, lng: 129.0756 },
          { name: "해운대구", lat: 35.1631, lng: 129.1641 },
          { name: "수영구", lat: 35.1455, lng: 129.1127 },
          { name: "남구", lat: 35.1364, lng: 129.0842 },
          { name: "동래구", lat: 35.2049, lng: 129.0837 },
          { name: "연제구", lat: 35.1763, lng: 129.0794 },
          { name: "부산진구", lat: 35.1622, lng: 129.0532 },
          { name: "동구", lat: 35.1295, lng: 129.0456 },
          { name: "서구", lat: 35.0969, lng: 129.0239 },
          { name: "영도구", lat: 35.0906, lng: 129.0678 },
          { name: "중구", lat: 35.1067, lng: 129.0323 },

          // 대구광역시
          { name: "대구", lat: 35.8714, lng: 128.6014 },
          { name: "대구광역시", lat: 35.8714, lng: 128.6014 },
          { name: "수성구", lat: 35.8581, lng: 128.6311 },
          { name: "달서구", lat: 35.83, lng: 128.5326 },
          { name: "서구", lat: 35.8719, lng: 128.5593 },
          { name: "남구", lat: 35.846, lng: 128.5975 },
          { name: "북구", lat: 35.8858, lng: 128.5828 },
          { name: "동구", lat: 35.8896, lng: 128.6355 },
          { name: "중구", lat: 35.8683, lng: 128.6058 },

          // 인천광역시
          { name: "인천", lat: 37.4563, lng: 126.7052 },
          { name: "인천광역시", lat: 37.4563, lng: 126.7052 },
          { name: "남동구", lat: 37.4469, lng: 126.7312 },
          { name: "연수구", lat: 37.4108, lng: 126.6782 },
          { name: "계양구", lat: 37.5377, lng: 126.7377 },
          { name: "부평구", lat: 37.5078, lng: 126.7214 },
          { name: "서구", lat: 37.5456, lng: 126.6756 },
          { name: "미추홀구", lat: 37.4636, lng: 126.6502 },

          // 광주광역시
          { name: "광주", lat: 35.1595, lng: 126.8526 },
          { name: "광주광역시", lat: 35.1595, lng: 126.8526 },

          // 대전광역시
          { name: "대전", lat: 36.3504, lng: 127.3845 },
          { name: "대전광역시", lat: 36.3504, lng: 127.3845 },

          // 울산광역시
          { name: "울산", lat: 35.5384, lng: 129.3114 },
          { name: "울산광역시", lat: 35.5384, lng: 129.3114 },

          // 경기도
          { name: "경기도", lat: 37.4138, lng: 127.5183 },
          { name: "경기", lat: 37.4138, lng: 127.5183 },
          { name: "수원", lat: 37.2636, lng: 127.0286 },
          { name: "성남", lat: 37.4449, lng: 127.1388 },
          { name: "고양", lat: 37.6584, lng: 126.832 },
          { name: "용인", lat: 37.241, lng: 127.1776 },
          { name: "부천", lat: 37.5035, lng: 126.766 },
          { name: "안산", lat: 37.3215, lng: 126.8309 },
          { name: "안양", lat: 37.3943, lng: 126.9568 },
          { name: "남양주", lat: 37.6365, lng: 127.2165 },
          { name: "화성", lat: 37.1997, lng: 126.8311 },
          { name: "평택", lat: 36.9921, lng: 127.1127 },
          { name: "의정부", lat: 37.7381, lng: 127.0338 },
          { name: "시흥", lat: 37.38, lng: 126.8028 },
          { name: "파주", lat: 37.7598, lng: 126.78 },
          { name: "광명", lat: 37.4784, lng: 126.8644 },
          { name: "김포", lat: 37.6153, lng: 126.7157 },
          { name: "군포", lat: 37.3615, lng: 126.9352 },
          { name: "하남", lat: 37.5394, lng: 127.2145 },
          { name: "오산", lat: 37.1499, lng: 127.0776 },
          { name: "이천", lat: 37.2792, lng: 127.4348 },
          { name: "안성", lat: 37.0078, lng: 127.2797 },
          { name: "의왕", lat: 37.3447, lng: 126.9683 },
          { name: "양평", lat: 37.4911, lng: 127.4874 },
          { name: "여주", lat: 37.2982, lng: 127.6373 },
          { name: "과천", lat: 37.4291, lng: 126.9872 },

          // 강원도
          { name: "강원도", lat: 37.8228, lng: 128.1555 },
          { name: "강원", lat: 37.8228, lng: 128.1555 },
          { name: "춘천", lat: 37.8813, lng: 127.7299 },
          { name: "원주", lat: 37.3422, lng: 127.9202 },
          { name: "강릉", lat: 37.7519, lng: 128.8761 },
          { name: "동해", lat: 37.5244, lng: 129.1144 },
          { name: "태백", lat: 37.164, lng: 128.9856 },
          { name: "속초", lat: 38.207, lng: 128.5918 },
          { name: "삼척", lat: 37.4499, lng: 129.1658 },

          // 충청북도
          { name: "충청북도", lat: 36.6357, lng: 127.4914 },
          { name: "충북", lat: 36.6357, lng: 127.4914 },
          { name: "청주", lat: 36.6424, lng: 127.489 },
          { name: "충주", lat: 36.991, lng: 127.9259 },
          { name: "제천", lat: 37.1326, lng: 128.1907 },

          // 충청남도
          { name: "충청남도", lat: 36.5184, lng: 126.8 },
          { name: "충남", lat: 36.5184, lng: 126.8 },
          { name: "천안", lat: 36.8151, lng: 127.1139 },
          { name: "아산", lat: 36.7898, lng: 127.0017 },
          { name: "서산", lat: 36.7848, lng: 126.4503 },
          { name: "당진", lat: 36.8947, lng: 126.6295 },
          { name: "공주", lat: 36.4465, lng: 127.1189 },
          { name: "보령", lat: 36.333, lng: 126.6128 },

          // 전라북도
          { name: "전라북도", lat: 35.7175, lng: 127.153 },
          { name: "전북", lat: 35.7175, lng: 127.153 },
          { name: "전주", lat: 35.8242, lng: 127.148 },
          { name: "군산", lat: 35.9676, lng: 126.7369 },
          { name: "익산", lat: 35.9483, lng: 126.9576 },
          { name: "정읍", lat: 35.5697, lng: 126.8559 },
          { name: "남원", lat: 35.4163, lng: 127.3906 },
          { name: "김제", lat: 35.8035, lng: 126.8805 },

          // 전라남도
          { name: "전라남도", lat: 34.8679, lng: 126.991 },
          { name: "전남", lat: 34.8679, lng: 126.991 },
          { name: "목포", lat: 34.8118, lng: 126.3922 },
          { name: "여수", lat: 34.7604, lng: 127.6622 },
          { name: "순천", lat: 34.9507, lng: 127.4872 },
          { name: "나주", lat: 35.016, lng: 126.7109 },
          { name: "광양", lat: 34.9407, lng: 127.5956 },

          // 경상북도
          { name: "경상북도", lat: 36.4919, lng: 128.8889 },
          { name: "경북", lat: 36.4919, lng: 128.8889 },
          { name: "포항", lat: 36.019, lng: 129.3435 },
          { name: "경주", lat: 35.8562, lng: 129.2247 },
          { name: "김천", lat: 36.1396, lng: 128.1137 },
          { name: "안동", lat: 36.5684, lng: 128.7294 },
          { name: "구미", lat: 36.1196, lng: 128.3441 },
          { name: "영주", lat: 36.8058, lng: 128.624 },
          { name: "영천", lat: 35.9732, lng: 128.9388 },
          { name: "상주", lat: 36.4107, lng: 128.1587 },
          { name: "문경", lat: 36.5867, lng: 128.1864 },
          { name: "경산", lat: 35.8251, lng: 128.7411 },

          // 경상남도
          { name: "경상남도", lat: 35.4606, lng: 128.2132 },
          { name: "경남", lat: 35.4606, lng: 128.2132 },
          { name: "창원", lat: 35.228, lng: 128.6811 },
          { name: "김해", lat: 35.2285, lng: 128.889 },
          { name: "진주", lat: 35.18, lng: 128.1076 },
          { name: "양산", lat: 35.335, lng: 129.0371 },
          { name: "거제", lat: 34.8806, lng: 128.6217 },
          { name: "통영", lat: 34.8544, lng: 128.4332 },
          { name: "사천", lat: 35.0037, lng: 128.0644 },
          { name: "밀양", lat: 35.5041, lng: 128.7463 },

          // 제주특별자치도
          { name: "제주특별자치도", lat: 33.4996, lng: 126.5312 },
          { name: "제주도", lat: 33.4996, lng: 126.5312 },
          { name: "제주", lat: 33.4996, lng: 126.5312 },
          { name: "서귀포", lat: 33.2541, lng: 126.56 },
        ];

        // 검색어를 공백으로 분리하여 더 유연한 지역 검색
        const searchWords = query
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0)
          .map((word) => word.toLowerCase());

        const matchedRegions = basicRegions.filter((region) => {
          const regionName = region.name.toLowerCase();

          // 모든 검색어가 지역명에 포함되는지 확인 (AND 검색)
          return (
            searchWords.every((word) => regionName.includes(word)) ||
            // 또는 전체 검색어가 지역명에 포함되는지 확인
            regionName.includes(query.toLowerCase()) ||
            // 또는 지역명이 검색어에 포함되는지 확인
            query.toLowerCase().includes(regionName)
          );
        });

        const mockResults: NaverAddressResult[] = matchedRegions.map(
          (region) => ({
            title: `${region.name} 지역`,
            link: "",
            category: "지역",
            description: `${region.name} 주변 지역입니다`,
            telephone: "",
            address: `${region.name}`,
            roadAddress: `${region.name}`,
            mapx: (region.lng * 10000000).toString(),
            mapy: (region.lat * 10000000).toString(),
          })
        );

        setAddressResults(mockResults);
        return;
      }

      console.log(`네이버 지역 검색: ${query}`);

      // 네이버 지오코딩 API를 사용하여 주소 검색
      window.naver.maps.Service.geocode(
        {
          query: query,
        },
        function (status: any, response: any) {
          if (status !== window.naver.maps.Service.Status.OK) {
            console.error("주소 검색 실패:", status);
            setAddressResults([]);
            return;
          }

          try {
            const results = response.v2?.addresses || [];

            if (results.length === 0) {
              console.log("검색 결과가 없습니다.");
              setAddressResults([]);
              return;
            }

            // 검색 결과를 NaverAddressResult 형식으로 변환
            const mappedResults: NaverAddressResult[] = results
              .slice(0, 20)
              .map((addr: any, index: number) => ({
                title: addr.roadAddress || addr.jibunAddress || query,
                link: "",
                category: "지역",
                description: `${query} 검색 결과`,
                telephone: "",
                address: addr.jibunAddress || "",
                roadAddress: addr.roadAddress || addr.jibunAddress || "",
                mapx: (parseFloat(addr.x) * 10000000).toString(), // 네이버 좌표계로 변환
                mapy: (parseFloat(addr.y) * 10000000).toString(),
              }));

            console.log("주소 검색 결과:", mappedResults);
            setAddressResults(mappedResults);
          } catch (error) {
            console.error("주소 검색 결과 처리 중 오류:", error);
            setAddressResults([]);
          }
        }
      );
    } catch (error) {
      console.error("네이버 지역 검색 오류:", error);
      setAddressResults([]);
    }
  };

  // 매장명 검색
  const searchStores = (query: string) => {
    if (!query.trim()) {
      setStoreResults([]);
      return;
    }

    // 검색어를 공백으로 분리하여 각 단어별로 검색
    const searchWords = query
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0)
      .map((word) => word.toLowerCase());

    if (searchWords.length === 0) {
      setStoreResults([]);
      return;
    }

    const filtered = allStoreData.filter((store) => {
      const storeName = store.name.toLowerCase();
      const storeAddress = store.address.toLowerCase();
      const storeType = store.type.toLowerCase();

      // 모든 검색어가 이름, 주소, 또는 타입 중 어디든 포함되는지 확인 (AND 검색)
      return searchWords.every(
        (word) =>
          storeName.includes(word) ||
          storeAddress.includes(word) ||
          storeType.includes(word)
      );
    });

    // 정확도 순으로 정렬
    const sorted = filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aAddress = a.address.toLowerCase();
      const bAddress = b.address.toLowerCase();

      // 전체 검색어가 이름에서 정확히 일치하는 것이 최우선
      const fullQueryLower = query.toLowerCase();
      const aFullNameMatch = aName.indexOf(fullQueryLower);
      const bFullNameMatch = bName.indexOf(fullQueryLower);

      if (aFullNameMatch === 0 && bFullNameMatch !== 0) return -1;
      if (bFullNameMatch === 0 && aFullNameMatch !== 0) return 1;

      // 전체 검색어가 이름에 포함되는 것이 다음 우선순위
      if (aFullNameMatch !== -1 && bFullNameMatch === -1) return -1;
      if (bFullNameMatch !== -1 && aFullNameMatch === -1) return 1;

      // 첫 번째 검색어가 이름에서 시작하는 것이 우선
      const firstWord = searchWords[0];
      const aFirstWordMatch = aName.indexOf(firstWord);
      const bFirstWordMatch = bName.indexOf(firstWord);

      if (aFirstWordMatch === 0 && bFirstWordMatch !== 0) return -1;
      if (bFirstWordMatch === 0 && aFirstWordMatch !== 0) return 1;

      // 더 많은 검색어가 이름에 포함되는 것이 우선
      const aNameMatches = searchWords.filter((word) =>
        aName.includes(word)
      ).length;
      const bNameMatches = searchWords.filter((word) =>
        bName.includes(word)
      ).length;

      if (aNameMatches !== bNameMatches) return bNameMatches - aNameMatches;

      // 이름에 포함되는 것이 주소에만 포함되는 것보다 우선
      const aInName = searchWords.some((word) => aName.includes(word));
      const bInName = searchWords.some((word) => bName.includes(word));

      if (aInName && !bInName) return -1;
      if (bInName && !aInName) return 1;

      return 0;
    });

    setStoreResults(sorted.slice(0, 1000)); // 최대 1000개 결과
  };

  // 검색 실행
  const performSearch = (query: string) => {
    if (!query.trim()) {
      setAddressResults([]);
      setStoreResults([]);
      setHasSearched(false);
      // 페이지네이션 상태 초기화
      setDisplayedAddressCount(ITEMS_PER_PAGE);
      setDisplayedStoreCount(ITEMS_PER_PAGE);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    // 페이지네이션 상태 초기화
    setDisplayedAddressCount(ITEMS_PER_PAGE);
    setDisplayedStoreCount(ITEMS_PER_PAGE);

    // 네이버 지역 검색과 매장 검색을 동시에 실행
    Promise.all([searchNaverPlaces(query), searchStores(query)]).finally(() => {
      setIsSearching(false);
    });
  };

  // 검색어 입력 처리 (디바운싱)
  const handleSearchInput = (value: string) => {
    setSearchQuery(value);

    // 기존 타이머 취소
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 300ms 후 검색 실행
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // 검색 결과 클릭 처리
  const handleAddressResultClick = (result: NaverAddressResult) => {
    const lat = parseInt(result.mapy) / 10000000; // 네이버 좌표계 변환
    const lng = parseInt(result.mapx) / 10000000;

    // 최근 검색어에 저장
    saveRecentSearchItem({
      id: `address-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: result.title,
      address: result.roadAddress || result.address,
      type: "address",
      lat,
      lng,
      searchedAt: Date.now(),
    });

    // 지도 페이지로 이동하면서 위치 정보 전달
    navigate("/map", {
      state: {
        searchLocation: {
          lat,
          lng,
          name: result.title,
          address: result.roadAddress || result.address,
        },
      },
    });
  };

  const handleStoreResultClick = (store: SearchResult) => {
    // 최근 검색어에 저장
    saveRecentSearchItem({
      id: store.id,
      name: store.name,
      address: store.address,
      type: "store",
      lat: store.lat,
      lng: store.lng,
      storeType: store.type,
      searchedAt: Date.now(),
    });

    // 지도 페이지로 이동하면서 매장 정보 전달
    navigate("/map", {
      state: {
        selectedStore: store,
        searchLocation: {
          lat: store.lat,
          lng: store.lng,
          name: store.name,
          address: store.address,
        },
      },
    });
  };

  // 검색어 하이라이트 함수
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;

    // 검색어를 공백으로 분리하여 각 단어별로 하이라이트
    const searchWords = searchTerm
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0)
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")); // 정규식 특수문자 이스케이프

    if (searchWords.length === 0) return text;

    // 모든 검색어를 하나의 정규식으로 결합 (대소문자 무시)
    const regex = new RegExp(`(${searchWords.join("|")})`, "gi");

    const parts = text.split(regex);

    return parts.map((part, index) => {
      // 검색어 중 하나와 일치하는지 확인 (대소문자 무시)
      const isMatch = searchWords.some((word) => {
        const unescapedWord = word.replace(/\\(.)/g, "$1"); // 이스케이프된 문자 원복
        return part.toLowerCase() === unescapedWord.toLowerCase();
      });

      if (isMatch) {
        return (
          <span key={index} className={styles["highlight"]}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // 더보기 버튼 핸들러
  const handleLoadMoreAddresses = () => {
    setDisplayedAddressCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const handleLoadMoreStores = () => {
    setDisplayedStoreCount((prev) => prev + ITEMS_PER_PAGE);
  };

  // 최근 검색 항목 클릭 - 해당 좌표로 이동
  const handleRecentSearchClick = (item: RecentSearchItem) => {
    // 지도 페이지로 이동하면서 위치 정보 전달
    navigate("/map", {
      state: {
        searchLocation: {
          lat: item.lat,
          lng: item.lng,
          name: item.name,
          address: item.address,
        },
        ...(item.type === "store" && {
          selectedStore: {
            id: item.id,
            name: item.name,
            address: item.address,
            type: item.storeType || "매장",
            lat: item.lat,
            lng: item.lng,
          },
        }),
      },
    });
  };

  // 개별 최근 검색 항목 삭제
  const handleDeleteRecentItem = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = recentSearches.filter((item) => item.id !== itemId);
    setRecentSearches(updated);
    localStorage.setItem("mapRecentSearches", JSON.stringify(updated));
  };

  return (
    <div className={styles["search-page"]}>
      {/* 헤더 */}
      <div className={styles["search-header"]}>
        <button className={styles["back-button"]} onClick={() => navigate(-1)}>
          <img src="/images/icons/arrow-left.svg" alt="뒤로가기" />
        </button>
        <div className={styles["search-input-container"]}>
          <img
            src="/images/icons/map-search.svg"
            alt="검색"
            className={styles["search-icon"]}
          />
          <input
            type="text"
            placeholder="지역 및 상호명을 검색해주세요"
            className={styles["search-input"]}
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              className={styles["clear-button"]}
              onClick={() => {
                setSearchQuery("");
                setAddressResults([]);
                setStoreResults([]);
                setHasSearched(false);
                // 페이지네이션 상태 초기화
                setDisplayedAddressCount(ITEMS_PER_PAGE);
                setDisplayedStoreCount(ITEMS_PER_PAGE);
              }}
            >
              <img src="/images/icons/map-close.svg" alt="지우기" />
            </button>
          )}
        </div>
      </div>

      {/* 검색 결과 영역 */}
      <div className={styles["search-content"]}>
        {isSearching && (
          <div className={styles["loading-container"]}>
            <div className={styles["loading-spinner"]}></div>
            <span>검색 중...</span>
          </div>
        )}

        {!isSearching && !hasSearched && (
          <div className={styles["initial-state"]}>
            {/* 최근 검색어 */}
            {recentSearches.length > 0 && (
              <div className={styles["recent-searches"]}>
                <div className={styles["recent-header"]}>
                  <h3>최근검색</h3>
                  <button
                    className={styles["clear-all-button"]}
                    onClick={clearAllRecentSearches}
                  >
                    전체삭제
                  </button>
                </div>
                <div className={styles["recent-list"]}>
                  {recentSearches.map((item) => (
                    <div
                      key={item.id}
                      className={styles["recent-item"]}
                      onClick={() => handleRecentSearchClick(item)}
                    >
                      <div className={styles["recent-item-content"]}>
                        <span className={styles["recent-item-name"]}>
                          {item.name}
                        </span>
                        {item.type !== "address" && (
                          <span className={styles["recent-item-address"]}>
                            {item.address}
                          </span>
                        )}
                      </div>
                      <button
                        className={styles["delete-recent"]}
                        onClick={(e) => handleDeleteRecentItem(item.id, e)}
                      >
                        <img src="/images/icons/map-close.svg" alt="삭제" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 빈 상태 */}
            {recentSearches.length === 0 && (
              <div className={styles["empty-state"]}>
                <div className={styles["empty-icon"]}>
                  <img src="/images/assets/search-empty.svg" alt="empty" />
                </div>
                <p>
                  최근검색 기록이 없어요 <br />
                  지역 또는 상호명을 검색해 주세요!
                </p>
              </div>
            )}
          </div>
        )}

        {!isSearching && hasSearched && (
          <div className={styles["search-results"]}>
            {/* 지역 검색 결과 */}
            {addressResults.length > 0 && (
              <div className={styles["address-results"]}>
                <h3>
                  <img src="/images/icons/profile-pin.svg" alt="검색" />
                  지역({addressResults.length})
                </h3>
                <div className={styles["results-list"]}>
                  <div className={styles["address-item"]}>
                    {addressResults
                      .slice(0, displayedAddressCount)
                      .map((result, index) => (
                        <div
                          key={index}
                          className={styles["address-info"]}
                          onClick={() => handleAddressResultClick(result)}
                        >
                          <p>
                            {highlightSearchTerm(
                              result.roadAddress || result.address,
                              searchQuery
                            )}
                          </p>
                        </div>
                      ))}
                    {/* 더보기 버튼 */}
                    {addressResults.length > displayedAddressCount && (
                      <button
                        className={styles["load-more-button"]}
                        onClick={handleLoadMoreAddresses}
                      >
                        더보기
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 매장 검색 결과 */}
            {storeResults.length > 0 && (
              <div className={styles["store-results"]}>
                <h3>
                  <img src="/images/icons/profile-pin.svg" alt="검색" />
                  상호명({storeResults.length})
                </h3>
                <div className={styles["results-list"]}>
                  <div className={styles["store-item"]}>
                    {storeResults.slice(0, displayedStoreCount).map((store) => (
                      <div
                        key={store.id}
                        className={styles["store-info"]}
                        onClick={() => handleStoreResultClick(store)}
                      >
                        <div className={styles["store-header"]}>
                          <h4>
                            {highlightSearchTerm(store.name, searchQuery)}
                          </h4>
                        </div>
                        <p>{highlightSearchTerm(store.address, searchQuery)}</p>
                      </div>
                    ))}

                    {/* 더보기 버튼 */}
                    {storeResults.length > displayedStoreCount && (
                      <button
                        className={styles["load-more-button"]}
                        onClick={handleLoadMoreStores}
                      >
                        더보기
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 검색 결과 없음 */}
            {addressResults.length === 0 && storeResults.length === 0 && (
              <div className={styles["no-results"]}>
                <div className={styles["empty-icon"]}>
                  <img src="/images/assets/search-empty.svg" alt="empty" />
                </div>
                <p>
                  검색결과가 없어요
                  <br /> 다시 입력해 주세요
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapSearchPage;
